import {useMemo} from 'react'
import {useDiffLineContext} from '../contexts/DiffLineContext'
import {useSelectedDiffRowRangeContext} from '../contexts/SelectedDiffRowRangeContext'
import type {DiffLine} from '../types'

/**
 * A React hook that determines whether the current diff line is the last line
 * in the currently selected range of diff rows.
 *
 * This hook compares the current line's position (from DiffLineContext) with the end position
 * of the selected range (from SelectedDiffRowRangeContext). It checks both left and right orientations
 * to support both unified and split diff views.
 *
 * Please note: This will trigger a re-render of the consuming component when `selectedDiffRowRange` value changes in the `SelectedDiffRowRangeContext`.
 * This is unfortunate, but it's likely the component that consumes this already has a dependency on that context and will re-render already.
 * Future work to port `SelectedDiffRowRangeContext` to Zustand in https://github.com/github/pull-requests/issues/14585 will allow this function to work differently and more performant.
 *
 * @returns {boolean} Returns `true` if the current diff line is the last line
 *                    in the currently selected range, otherwise returns `false`.
 *
 * @example
 * ```tsx
 * function CodeCell() {
 *   const lineIsLastSelectedLine = useLineIsLastSelectedLine();
 *
 *   return (
 *     <td>
 *        {lineIsLastSelectedLine && (<div>Show last selected line content)}
 *     </td>
 *   );
 * }
 * ```
 */
export function useLineIsLastSelectedLine(): boolean {
  const {diffLine, isLeftSide} = useDiffLineContext()
  const line = diffLine as DiffLine
  const {selectedDiffRowRange} = useSelectedDiffRowRangeContext()

  return useMemo(() => {
    // If there is no selected diff row range, than we can early return with `false`
    if (!selectedDiffRowRange) {
      return false
    }

    const isRightSide = !isLeftSide

    if (
      isRightSide &&
      selectedDiffRowRange.endOrientation === 'right' &&
      selectedDiffRowRange.endLineNumber === line.right
    ) {
      return true
    }

    // If the previous conditionals don't return `true` and the line is on the right side of the diff we can safely return `false`
    if (isRightSide) {
      return false
    }

    if (selectedDiffRowRange.endOrientation === 'left' && selectedDiffRowRange.endLineNumber === line.left) {
      return true
    }

    return false
  }, [isLeftSide, line.left, line.right, selectedDiffRowRange])
}
